// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { extractErrorMessage, useEvolvePost } from '@evolvemep/api-ts';
import { Anchor, Button, Flex, Text, useMantineTheme } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router';

import { EvolveIcon } from 'assets/icons/EvolveIcon';

import { AuthFormWrapper } from '../components/AuthFormWrapper';
import type { LoginErrorCode } from '../components/LoginErrorAlert';

export const CheckEmailForPasswordReset = ({ email }: { email: string }) => {
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<LoginErrorCode | string>();
  const { apiCall: sendPasswordResetEmail } = useEvolvePost('admin/authentication/forgotPassword', {
    alertOnError: false,
  });

  const resendEmailLink = async () => {
    setErrorCode(undefined);
    try {
      setLoading(true);
      await sendPasswordResetEmail({ email });
      notifications.show({
        title: 'Email resent',
        message: 'Password reset email resent!',
        color: 'green',
      });
    } catch (error) {
      setErrorCode(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthFormWrapper
      title="Check your email!"
      onSubmit={resendEmailLink}
      errorCode={errorCode}
      onCloseError={() => setErrorCode(undefined)}
    >
      <Text>If we have an account associated with {email}, a password reset link has been sent.</Text>
      <Button type="submit" loading={loading} fullWidth color={`${theme.primaryColor}.9`}>
        Resend link
      </Button>
      <Link
        className="link"
        data-cy="forgot-password"
        to={{
          pathname: '/auth/login',
          search: `?email=${encodeURIComponent(email)}`,
        }}
      >
        <Flex justify="center" align="center" gap={4}>
          <EvolveIcon icon="ArrowLeft" size={10} />
          <Text c="dimmed" fz="sm">
            Return to login
          </Text>
        </Flex>
      </Link>
      <Text ta="center" variant="body1">
        Questions or problems?{' '}
        <Anchor className="link" href="https://www.evolvemep.com/contact-support/" target="_blank">
          Get in touch.
        </Anchor>
      </Text>
    </AuthFormWrapper>
  );
};
